/**
 * Interop helper for importing lambda/ modules from server/ code.
 *
 * The lambda workspace (web-app/lambda) has no `"type": "module"`, so under tsx (which
 * `npm run preview` / `hono-entry.node.ts` run on) it compiles to CJS, and an ESM named import
 * only sees `{ default, 'module.exports' }` — named imports throw at runtime (real production
 * Lambda deploys never hit this: CDK bundles each function with esbuild, which resolves the
 * interop correctly at bundle time). Under vitest the same files transform straight to ESM, so
 * the real exports are already on the namespace object with no `.default` wrapper — and a
 * `vi.mock()`'d version of the module throws on accessing any property its factory didn't return,
 * `default` included, so this must check for `default`'s presence with the `in` operator (which
 * doesn't trigger that) before ever reading it.
 *
 * Usage: `import * as fooNs from '../../lambda/foo'; const { bar } = unwrapLambdaModule(fooNs);`
 */
export function unwrapLambdaModule<T extends object>(ns: T): T {
  if (!('default' in ns)) return ns;
  const maybeDefault = (ns as { default?: unknown }).default;
  return maybeDefault && typeof maybeDefault === 'object' ? (maybeDefault as T) : ns;
}
